import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { Tooltip } from 'react-tooltip';
import { Pagination } from "../../../../../../../common/pagination";
import ModernPagination from "../../../../../../../common/pagination/ModernPagination";
import Table from "../../../../../../../common/element/Table";
import ClientInput from "../../../../../../../common/element/Input/clientInput";
import BtnPrimary from "../../../../../../../common/element/Buttons/btnPrimary";
import Select from "../../../../../../../common/element/Select";
import Switcher from "../../../../../../../common/element/Switcher";
import { debounce } from "../../../../../../../common/snippet/Debouncing";
import { setBreadcrumbs } from "../../../../../../../redux/Admin/breadcrumbsSlice";
import { useDispatch, useSelector } from 'react-redux';
import ToolManagmentCard from "../../../../../../../common/element/Card/ToolManagmnetCard";
import { selectUserData } from "../../../../../../../redux/auth/authSelector";
import { ROLES } from "../../../../../../../config/rolePermissions";
import { useLocation, useNavigate } from "react-router-dom";
import Button from '../../../../../../../common/element/Buttons';
import ConfirmationModal from '../../../../../../../common/Common/ConfirmationModal';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  getCompanySessions,
  enableCompanySession,
  enableToolAccess,
  enableSupportDocument,
  enableAssignment
} from '../../../../../../../redux/CompanySession/companiesSessionSlice';
import {
  selectCompanySessionsList,
  selectCompanySessionsLoading,
  selectCompanySessionsTotal,
} from '../../../../../../../redux/CompanySession/companiesSessionSelector';
import SweetAlertService from '../../../../../../../common/SweetAlert/SweetAlert';
import { formatToolName } from '../../../../../../../common/Formatters.js/formatters';
import { setsessionName } from '../../../../../../../redux/Admin/sessionViewSlice';
import CommonIconButton from '../../../../../../../common/icons/CommonIconButton';
import CopyLinkURLModal from '../../../../ProspectCompany/CopyLink';

// Extend dayjs with custom parse format plugin
dayjs.extend(customParseFormat);

const AddEditSession = lazy(() => import('./AddSession'));
const AddEditSessionView = lazy(() => import('../../../../Session Master/AddEdit'));
const { Tr, Th, Td, THead, TBody } = Table;

const BatchesSessions = ({ companyData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector(selectUserData);
  const isAdmin = userData?.role_name === ROLES.ADMIN;
  const isActiveCompany = companyData?.hasOwnProperty('company_isActive') ? companyData?.company_isActive : userData?.company_isActive;
  const [countData, setCountData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const location = useLocation();
  const [isViewMode, setIsViewMode] = useState(false);

  const SESSION_TYPE_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'normal_session', label: 'Normal Session' },
    { value: 'session_url', label: 'Session URL' },
  ];

  const [sessionTypeFilter, setSessionTypeFilter] = useState(SESSION_TYPE_OPTIONS[0]);

  // Loading states for individual toggles
  const [toolToggleLoading, setToolToggleLoading] = useState({});
  const [docToggleLoading, setDocToggleLoading] = useState({});
  const [assignmentLoading, setAssignmentLoading] = useState({});

  const searchParams = new URLSearchParams(location.search);
  const rawCompanyId = searchParams.get("companyId");
  const companyId = rawCompanyId !== null ? Number(rawCompanyId) : null;

  const [getFilterData, SetFilterData] = useState({
    page: 1,
    limit: 10,
    sort: "created_date",
    order: "desc",
    search: "",
  });

  // State for confirmation modal
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    action: null, // Will store the function to call on "Yes"
  });

  const handleSearch = (e) => {
    SetFilterData((prevalue) => ({
      ...prevalue,
      [e.target.name]: e.target.value.trim(),
    }));
  };

  const inputchangehandler = useCallback(debounce(handleSearch), []);

  const [allSessionsData, setAllSessionsData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handlePageChange = (page) => {
    SetFilterData((prev) => ({ ...prev, page }));
  };

  // Helper: Format date only using dayjs
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const parsedDate = dayjs(dateString);
    return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : dateString;
  };

  // Build session_type param — omit key entirely for "All" (empty value)
  const buildSessionTypeParam = (filter) =>
    filter?.value ? { session_type: filter.value } : {};

  // Helper function to refresh sessions data
  const refreshSessionsData = () => {
    if (companyId) {
      dispatch(getCompanySessions({
        company_id: companyId,
        page: getFilterData.page,
        limit: getFilterData.limit,
        search: getFilterData.search,
        ...buildSessionTypeParam(sessionTypeFilter),
      }));
    }
  };

  // ✅ Enable Tool Access API Integration - Only allows enabling, not disabling
  const onToolToggle = async (checked, sessionId) => {
    if (!companyId) return;

    const sessionItem = allSessionsData.find(s => s.id === sessionId);
    if (!sessionItem) return;

    // ✅ Prevent disabling - only allow enabling
    if (!checked && sessionItem.toolEnabled) {
      SweetAlertService.showWarning({
        title: 'Action Not Allowed',
        message: 'Tool access cannot be disabled once enabled.'
      });
      return;
    }

    // ✅ Show confirmation modal before enabling
    openConfirmationModal(
      'Send Tool?',
      'Are you sure you want to send the tool for this session?',
      async () => {
        const rawData = sessionItem._raw;

        setToolToggleLoading(prev => ({ ...prev, [sessionId]: true }));

        try {
          const toolData = {
            company_session_id: rawData.company_session_id,
            tool_enabled: checked,
            tool_id: rawData.session?.tool?.id || rawData.tool_id,
            batch_ids: rawData.batches?.map(b => b.batch_id) || [],
            is_locked: !checked
          };

          const resultAction = await dispatch(enableToolAccess({ companyId, toolData }));

          if (enableToolAccess.fulfilled.match(resultAction)) {
            SweetAlertService.showSuccess({
              title: 'Success!',
              message: 'Tool access enabled successfully.'
            });
            refreshSessionsData();
          } else {
            const err = resultAction.payload || resultAction.error;
            SweetAlertService.showError({
              title: 'Error!',
              message: err?.message || err || 'Failed to enable tool access'
            });
          }
        } catch (err) {
          console.error('Failed to enable tool access', err);
          SweetAlertService.showError({
            title: 'Error!',
            message: err?.message || 'Failed to enable tool access'
          });
        } finally {
          setToolToggleLoading(prev => ({ ...prev, [sessionId]: false }));
        }
      }
    );
  };

  // ✅ Enable Support Document API Integration - Only allows enabling, not disabling
  const onResourceToggle = async (checked, sessionId) => {
    if (!companyId) return;

    const sessionItem = allSessionsData.find(s => s.id === sessionId);
    if (!sessionItem) return;

    // ✅ Prevent disabling - only allow enabling
    if (!checked && sessionItem.supportedDocEnabled) {
      SweetAlertService.showWarning({
        title: 'Action Not Allowed',
        message: 'Support document access cannot be disabled once enabled.'
      });
      return;
    }

    // ✅ Show confirmation modal before enabling
    openConfirmationModal(
      'Send Supported Documents?',
      'Are you sure you want to send the supported documents for this session?',
      async () => {
        const rawData = sessionItem._raw;

        setDocToggleLoading(prev => ({ ...prev, [sessionId]: true }));

        try {
          const documentData = {
            company_session_id: rawData.company_session_id,
            supported_doc_enabled: checked,
            supported_doc_id: rawData.supported_doc_id || rawData.session?.supported_doc_id,
            batch_ids: rawData.batches?.map(b => b.batch_id) || []
          };

          const resultAction = await dispatch(enableSupportDocument({ companyId, documentData }));

          if (enableSupportDocument.fulfilled.match(resultAction)) {
            SweetAlertService.showSuccess({
              title: 'Success!',
              message: 'Support document enabled successfully.'
            });
            refreshSessionsData();
          } else {
            const err = resultAction.payload || resultAction.error;
            SweetAlertService.showError({
              title: 'Error!',
              message: err?.message || err || 'Failed to enable support document'
            });
          }
        } catch (err) {
          console.error('Failed to enable support document', err);
          SweetAlertService.showError({
            title: 'Error!',
            message: err?.message || 'Failed to enable support document'
          });
        } finally {
          setDocToggleLoading(prev => ({ ...prev, [sessionId]: false }));
        }
      }
    );
  };

  // ✅ Enable Assignment API Integration - Only allows enabling (sending)
  const handleSendAssignment = async (sessionId) => {
    if (!companyId) return;

    const sessionItem = allSessionsData.find(s => s.id === sessionId);
    if (!sessionItem) return;

    // ✅ Prevent if already sent
    if (sessionItem.assignmentEnabled) {
      SweetAlertService.showWarning({
        title: 'Already Sent',
        message: 'Assignment has already been sent and cannot be resent.'
      });
      return;
    }

    // ✅ Show confirmation modal before enabling
    openConfirmationModal(
      'Send Assignment?',
      'Are you sure you want to send the assignment for this session?',
      async () => {
        const rawData = sessionItem._raw;

        setAssignmentLoading(prev => ({ ...prev, [sessionId]: true }));

        try {
          const assignmentIds = rawData.assignments?.map(a => a.assignment_id) || [];

          const assignmentData = {
            company_session_id: rawData.company_session_id,
            assignment_enabled: true,
            assignment_id: assignmentIds.length > 0 ? assignmentIds[0] : rawData.assignment_id,
            batch_ids: rawData.batches?.map(b => b.batch_id) || []
          };

          const resultAction = await dispatch(enableAssignment({ companyId, assignmentData }));

          if (enableAssignment.fulfilled.match(resultAction)) {
            SweetAlertService.showSuccess({
              title: 'Success!',
              message: 'Assignment sent successfully.'
            });
            refreshSessionsData();
          } else {
            const err = resultAction.payload || resultAction.error;
            SweetAlertService.showError({
              title: 'Error!',
              message: err?.message || err || 'Failed to send assignment'
            });
          }
        } catch (err) {
          console.error('Failed to send assignment', err);
          SweetAlertService.showError({
            title: 'Error!',
            message: err?.message || 'Failed to send assignment'
          });
        } finally {
          setAssignmentLoading(prev => ({ ...prev, [sessionId]: false }));
        }
      }
    );
  };

  const filteredData = useMemo(() => {
    if (!getFilterData.search) return allSessionsData;
    const searchValue = getFilterData.search.toLowerCase();

    return allSessionsData.filter(
      (item) =>
        item.sessionName.toLowerCase().includes(searchValue) ||
        item.coach.toLowerCase().includes(searchValue) ||
        item.status.toLowerCase().includes(searchValue) ||
        item.toolName.toLowerCase().includes(searchValue) ||
        item.date.toLowerCase().includes(searchValue)
    );
  }, [getFilterData.search, allSessionsData]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "text-green-600 bg-green-50 text-capitalize ";
      case "upcoming":
        return "text-blue-600 bg-blue-50 text-capitalize";
      case "cancelled":
        return "text-red-600 bg-red-50 text-capitalize";
      case "ongoing":
        return "text-[#CA8A04] bg-[#FEF9C3] text-capitalize";
      default:
        return "text-gray-600 bg-gray-50 text-capitalize";
    }
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogOpenView, setDialogOpenView] = useState(false);
  const [openCopyLinkModal, setOpenCopyLinkModal] = useState(false);
  const [selectedSessionLink, setSelectedSessionLink] = useState(null);

  const handleAddNew = () => {
    setDialogOpen(true);
  };

  const startIndex = (getFilterData.page - 1) * getFilterData.limit;

  const sessionsFromStore = useSelector(selectCompanySessionsList);
  const sessionsLoading = useSelector(selectCompanySessionsLoading);
  const sessionsTotal = useSelector(selectCompanySessionsTotal);
  useEffect(() => {
    const mapped = Array.isArray(sessionsFromStore)
      ? sessionsFromStore.map((cs) => {
        const session = cs.session || {};
        const coachUser = cs.coach?.coach?.user || {};
        const batches = Array.isArray(cs.batches) ? cs.batches.map((b) => b.batch_name).join(', ') : '';

        const assignments = Array.isArray(cs.assignments) ? cs.assignments : [];
        const assignmentNames = assignments.map(a => a.assignment_name?.trim()).filter(Boolean).join(', ');

        const formattedDate = formatDateTime(cs.session_date, cs.session_time);

        // Format status_changed_at datetime
        const statusChangedAt = cs.status_changed_at
          ? (() => {
            const parsed = dayjs(cs.status_changed_at);
            return parsed.isValid() ? parsed.format('DD/MM/YYYY') : cs.status_changed_at;
          })()
          : null;

        return {
          id: cs.company_session_id ?? session.session_id,
          sessionName: cs.session_name ?? '',
          date: formattedDate, // ✅ Using formatted date/time
          rawDate: cs.session_date, // Keep raw for reference
          rawTime: cs.session_time, // Keep raw for reference
          batch: batches,
          coach: `${coachUser.first_name ?? ''} ${coachUser.last_name ?? ''}`.trim(),
          status: cs.status ?? '',
          statusChangedAt, // ✅ Formatted status_changed_at
          toolName: session.tool?.tool ?? session.tool ?? '',
          supportedDocEnabled: !!cs.supported_doc_enabled,
          assignmentEnabled: !!cs.assignment_enabled, // This is the key we use for toggle logic
          assignStatus: !!cs.assign_status,
          toolEnabled: cs.tool_enabled === undefined ? false : !!cs.tool_enabled,
          assignments: assignments,
          assignmentNames: assignmentNames,
          homeworkActivity: cs.assignment_enabled ? assignmentNames || 'Sent' : 'Send',
          sessionUrl: cs.session_url || '',
          _raw: cs,
        };
      })
      : [];

    setAllSessionsData(mapped);
    setCountData(sessionsTotal || 0);
    const totalPagesFromApi = getFilterData.limit ? Math.ceil((sessionsTotal || 0) / getFilterData.limit) : 0;
    setTotalPages(totalPagesFromApi);
    setLoading(!!sessionsLoading);
  }, [sessionsFromStore, sessionsLoading, sessionsTotal]);

  useEffect(() => {
    if (!companyId) return;
    dispatch(getCompanySessions({
      company_id: companyId,
      page: getFilterData.page,
      limit: getFilterData.limit,
      search: getFilterData.search,
      ...buildSessionTypeParam(sessionTypeFilter),
    }));
  }, [companyId, getFilterData.page, getFilterData.limit, getFilterData.search, sessionTypeFilter]);

  const handleAssignStatus = async (companySessionId, status) => {
    if (!companyId) return;
    try {
      const body = {
        company_session_id:
          companySessionId,
        assign_status: status,
        ...(status === true && { mark_completed: status }),
      };
      const resultAction = await dispatch(enableCompanySession({ companyId, body }));
      if (enableCompanySession.fulfilled.match(resultAction)) {
        refreshSessionsData();
        const statusMessage = status === true ? 'Session completed successfully.' : 'Session cancelled successfully.';
        SweetAlertService.showSuccess({ message: statusMessage });
      } else {
        const err = resultAction.payload || resultAction.error;
        SweetAlertService.showError({ message: err?.message || err || 'Failed to update session status' });
      }
    } catch (err) {
      console.error('Failed to update session status', err);
      SweetAlertService.showError({ message: err?.message || 'Failed to update session status' });
    }
  };

  // Helper function to open confirmation modal
  const openConfirmationModal = (title, message, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      action: onConfirm,
    });
  };

  // Handle confirmation modal Yes button
  const handleConfirmationYes = async () => {
    if (confirmationModal.action) {
      await confirmationModal.action();
    }
    setConfirmationModal({
      isOpen: false,
      title: "",
      message: "",
      action: null,
    });
  };

  // Handle confirmation modal No button
  const handleConfirmationNo = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      message: "",
      action: null,
    });
  };

  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // ✅ Helper function to get tooltip content for toggles
  const getToggleTooltip = (type, item) => {
    // ✅ Check if company is active
    if (!isActiveCompany) {
      return 'Company is inactive, actions are disabled';
    }

    const status = item.status?.toLowerCase();

    // ✅ Priority Check: If cancelled
    if (status === 'cancelled') {
      return 'Session is cancelled, actions are disabled';
    }

    // ✅ Priority Check: If completed
    if (status === 'completed') {
      return 'Session is completed, actions are disabled';
    }

    if (type === 'docs') {
      if (!item._raw?.supported_documents?.length && !item.supportedDocEnabled) {
        return 'There is no supported document in this session';
      }
      if (item.supportedDocEnabled) {
        return 'Supported document already sent';
      }
      return 'Send the supported document';
    }
    if (type === 'assignment') {
      if (!item.assignments?.length) {
        return 'There is no assessment in this session';
      }
      if (item.assignmentEnabled) {
        return 'Assignment already sent';
      }
      return 'Send assignment';
    }
    if (type === 'tool') {
      if (!item.toolName) {
        return 'There is no tool in this session';
      }
      if (item.toolEnabled) {
        return 'Tool already sent';
      }
      return 'Send tool';
    }
    return '';
  };

  return (
    <React.Fragment>
      {/* ✅ Global Tooltip Component */}
      <Tooltip
        id="session-tooltip"
        place="top"
        className="!bg-gray-900 !text-white !text-xs !rounded-lg !px-3 !py-2 !z-50 !max-w-[250px]"
      />

      <div className="flex justify-between items-center">
        <p className="font-lato not-italic font-semibold text-[30px] leading-[38px] text-[#101828] my-3 text-start"></p>
        <div className="input-wrapper mb-4">
          <div className="input-suffix-start left-3.5">
            <img src="/assets/icons-svg/search-lg.svg" alt="" className="h-5" />
          </div>
          <ClientInput
            type="text"
            className="input pl-10 w-80 input-shadow h-10 text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="Search"
            inputchangehandler={inputchangehandler}
            name={"search"}
          />
        </div>
      </div>

      <ToolManagmentCard>
        <div className="flex justify-between items-center">
          <h4 className="font-lato not-italic font-semibold text-[16px] leading-[150%] text-[#101828] text-start mb-4">Sessions</h4>

          <div className="flex gap-3 items-center">
            {/* Session Type Filter */}
            <div className="mb-4 w-[150px]">
              <Select
                options={SESSION_TYPE_OPTIONS}
                value={sessionTypeFilter}
                onChange={(opt) => {
                  setSessionTypeFilter(opt || SESSION_TYPE_OPTIONS[0]);
                  SetFilterData((prev) => ({ ...prev, page: 1 }));
                }}
                placeholder="Select Type"
              />
            </div>

            {(isAdmin && isActiveCompany) && (
              <BtnPrimary
                onClick={handleAddNew}
                className="mb-4 bg-[#FF8E1C] hover:bg-[#FF8E1C] text-white px-2 py-2 rounded-lg flex items-center gap-2 w-40"
              >
                Add Sessions
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </BtnPrimary>
            )}
          </div>
        </div>

        <div className="gap-y-4 border-t border-gray-200 py-2"></div>

        <div className="border rounded-lg border-gray-200">
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <Tr className="bg-gray-50">
                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">S No.</Th>
                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Session Name</Th>
                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Date</Th>
                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Batch</Th>
                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Coach</Th>
                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Status</Th>
                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Tool Name</Th>

                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Tool </Th>
                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Supported Documents</Th>
                  <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Assignment</Th>
                  <Th className="text-center px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Status</Th>
                  <Th className="text-center px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Actions</Th>
                </Tr>
              </THead>

              <TBody>
                {filteredData?.length > 0 ? (
                  filteredData.map((item, index) => {
                    const status = item.status?.toLowerCase();
                    const isCompanyActive = isActiveCompany;
                    // ✅ Updated to include company inactive check
                    const areActionsDisabled = status === 'completed' || status === 'cancelled' || !isCompanyActive;

                    return (
                      <Tr key={item.id} className="border-b hover:bg-gray-50">
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                          {String(startIndex + index + 1).padStart(2, "0")}
                        </Td>
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">{item.sessionName ? item.sessionName.charAt(0).toUpperCase() + item.sessionName.slice(1) : ''}</Td>
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">{item.date}</Td>
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">{item.batch}</Td>
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">{item.coach ? item.coach : "N/A"}</Td>
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                          <span
                            className={`inline-block px-3 py-1 text-sm font-medium rounded-full capitalize ${getStatusColor(item.status)}`}
                          >
                            {item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : ''}
                          </span>
                        </Td>
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">{formatToolName(item.toolName)}</Td>


                        {/* ✅ Tool Toggle */}
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {toolToggleLoading[item.id] ? (
                              <div className="w-10 h-5 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#FF8E1C]"></div>
                              </div>
                            ) : (
                              // <div
                              //   data-tooltip-id="session-tooltip"
                              //   data-tooltip-content={getToggleTooltip('tool', item)}
                              // >
                              //   <Switcher
                              //     onChange={(checked) => onToolToggle(checked, item.id)}
                              //     checked={!!item.toolEnabled}
                              //     disabled={item.toolEnabled || areActionsDisabled}
                              //   />
                              // </div>
                              <div className="relative group inline-block">
                                <Switcher
                                  onChange={(checked) => onToolToggle(checked, item.id)}
                                  checked={!!item.toolEnabled}
                                  disabled={item.toolEnabled || areActionsDisabled || !item.toolName}
                                />
                                <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition pointer-events-none">
                                  <div className="bg-white text-black text-sm px-3 py-2 rounded-[8px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
                                    {getToggleTooltip('tool', item)}
                                  </div>
                                  <div className="w-2 h-2 bg-white rotate-45 absolute left-1/2 -translate-x-1/2 top-full -mt-1"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </Td>
                        {/* ✅ Supported Documents */}
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {docToggleLoading[item.id] ? (
                              <div className="w-10 h-5 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#FF8E1C]"></div>
                              </div>
                            ) : (
                              // <div
                              //   data-tooltip-id="session-tooltip"
                              //   data-tooltip-content={getToggleTooltip('docs', item)}
                              // >
                              //   <Switcher
                              //     onChange={(checked) => onResourceToggle(checked, item.id)}
                              //     checked={!!item.supportedDocEnabled}
                              //     disabled={item.supportedDocEnabled || areActionsDisabled}
                              //   />
                              // </div>
                              <div className="relative group inline-block">
                                <Switcher
                                  onChange={(checked) => onResourceToggle(checked, item.id)}
                                  checked={!!item.supportedDocEnabled}
                                  disabled={item.supportedDocEnabled || areActionsDisabled || (!item._raw?.supported_documents?.length && !item.supportedDocEnabled)}
                                />
                                <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition pointer-events-none">
                                  <div className="bg-white text-black text-sm px-3 py-2 rounded-[8px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
                                    {getToggleTooltip('docs', item)}
                                  </div>
                                  <div className="w-2 h-2 bg-white rotate-45 absolute left-1/2 -translate-x-1/2 top-full -mt-1"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </Td>

                        {/* ✅ Assignment Column */}
                        <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                          {item.assignmentEnabled ? (
                            // <span
                            //   data-tooltip-id="session-tooltip"
                            //   data-tooltip-content={item.assignmentNames && item.assignmentNames.length > 18 ? item.assignmentNames : ''}
                            // >
                            //   {truncateText(item.assignmentNames, 18)}
                            // </span>
                            <div className="relative group inline-block">
                              <span>
                                {truncateText(item.assignmentNames, 18)}
                              </span>

                              {item.assignmentNames && item.assignmentNames.length > 18 && (
                                <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                  <div className="bg-white text-black text-sm px-3 py-2 rounded-[8px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
                                    {item.assignmentNames}
                                  </div>
                                  <div className="w-2 h-2 bg-white rotate-45 absolute left-1/2 -translate-x-1/2 top-full -mt-1"></div>
                                </div>
                              )}
                            </div>
                          ) : (
                            // <div
                            //   data-tooltip-id="session-tooltip"
                            //   data-tooltip-content={getToggleTooltip('assignment', item)}
                            // >
                            //   <button
                            //     onClick={() => !areActionsDisabled && handleSendAssignment(item.id)}
                            //     className={`px-3 py-1 rounded text-xs flex items-center justify-center min-w-[50px] text-white ${areActionsDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#FF8E1C] hover:bg-[#E67D0F]'}`}
                            //     disabled={assignmentLoading[item.id] || areActionsDisabled}
                            //   >
                            //     {assignmentLoading[item.id] ? (
                            //       <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            //     ) : (
                            //       'Send'
                            //     )}
                            //   </button>
                            // </div>
                            <div className="relative group inline-block">
                              <button
                                onClick={() => !areActionsDisabled && item.assignments?.length > 0 && handleSendAssignment(item.id)}
                                className={`px-3 py-1 rounded text-xs flex items-center justify-center min-w-[50px] text-white ${areActionsDisabled || !item.assignments?.length
                                  ? 'bg-gray-400 cursor-not-allowed'
                                  : 'bg-[#FF8E1C] hover:bg-[#E67D0F]'
                                  }`}
                                disabled={assignmentLoading[item.id] || areActionsDisabled || !item.assignments?.length}
                              >
                                {assignmentLoading[item.id] ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                ) : (
                                  'Send'
                                )}
                              </button>

                              {/* Tooltip */}
                              <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                <div className="bg-white text-black text-sm px-3 py-2 rounded-[8px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
                                  {getToggleTooltip('assignment', item)}
                                </div>
                                <div className="w-2 h-2 bg-white rotate-45 absolute left-1/2 -translate-x-1/2 top-full -mt-1"></div>
                              </div>
                            </div>
                          )}
                        </Td>

                        {/* ✅ Status Column - Using react-tooltip */}
                        <Td className="px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                          <div className="flex gap-2 justify-center items-center">
                            {item.status?.toLowerCase() === 'cancelled' ? (
                              // <button
                              //   disabled
                              //   className="text-red-600 opacity-50 cursor-not-allowed"
                              //   aria-label="Session cancelled"
                              //   data-tooltip-id="session-tooltip"
                              //   data-tooltip-content={item.statusChangedAt ? `Cancelled on ${item.statusChangedAt}` : 'Cancelled'}
                              // >
                              //   <svg width="16" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              //     <path d="M13.6844 10L19.8296 3.88889C20.7235 3 20.7235 1.66667 19.8296 0.777778C19.4944 0.333333 18.9358 0 18.3771 0C17.8184 0 17.2598 0.222222 16.8129 0.666667L10.5559 6.88889L4.41061 0.777778C3.51676 -0.111111 2.06425 -0.111111 1.28212 0.777778C0.835195 1.11111 0.5 1.66667 0.5 2.33333C0.5 3 0.723464 3.44444 1.17039 3.88889L7.31564 10L1.17039 16.1111C0.835195 16.5556 0.5 17.1111 0.5 17.7778C0.5 18.3333 0.723464 18.8889 1.17039 19.3333C1.61732 19.7778 2.17598 20 2.73464 20C3.2933 20 3.85196 19.7778 4.29888 19.3333L10.4441 13.2222L16.5894 19.3333C17.4832 20.2222 18.9358 20.2222 19.7179 19.3333C20.6117 18.4444 20.6117 17 19.7179 16.2222L13.6844 10Z" fill="#B42318" />
                              //   </svg>
                              // </button>
                              <div className="relative group inline-block">
                                <button
                                  disabled
                                  className="text-red-600 opacity-50 cursor-not-allowed"
                                  aria-label="Session cancelled"
                                >
                                  <svg width="16" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13.6844 10L19.8296 3.88889C20.7235 3 20.7235 1.66667 19.8296 0.777778C19.4944 0.333333 18.9358 0 18.3771 0C17.8184 0 17.2598 0.222222 16.8129 0.666667L10.5559 6.88889L4.41061 0.777778C3.51676 -0.111111 2.06425 -0.111111 1.28212 0.777778C0.835195 1.11111 0.5 1.66667 0.5 2.33333C0.5 3 0.723464 3.44444 1.17039 3.88889L7.31564 10L1.17039 16.1111C0.835195 16.5556 0.5 17.1111 0.5 17.7778C0.5 18.3333 0.723464 18.8889 1.17039 19.3333C1.61732 19.7778 2.17598 20 2.73464 20C3.2933 20 3.85196 19.7778 4.29888 19.3333L10.4441 13.2222L16.5894 19.3333C17.4832 20.2222 18.9358 20.2222 19.7179 19.3333C20.6117 18.4444 20.6117 17 19.7179 16.2222L13.6844 10Z" fill="#B42318" />
                                  </svg>
                                </button>

                                {/* Tooltip */}
                                <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                  <div className="bg-white text-center text-black text-sm px-3 py-2 rounded-[8px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
                                    {item.statusChangedAt
                                      ? <>Cancelled on <br /> {item.statusChangedAt}</>
                                      : 'Cancelled'}
                                  </div>
                                  <div className="w-2 h-2 bg-white rotate-45 absolute left-1/2 -translate-x-1/2 top-full -mt-1"></div>
                                </div>
                              </div>
                            ) : (
                              <>
                                {item.status == "completed" ? (
                                  // <button
                                  //   disabled
                                  //   className="text-green-600 opacity-50 cursor-not-allowed"
                                  //   aria-label="Session completed"
                                  //   data-tooltip-id="session-tooltip"
                                  //   data-tooltip-content={item.statusChangedAt ? `Completed on ${item.statusChangedAt}` : 'Completed'}
                                  // >
                                  //   <svg width="18" height="18" viewBox="0 0 25 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  //     <path d="M2.5 10.7297L8.68692 17L22.5 3" stroke="#027A48" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
                                  //   </svg>
                                  // </button>
                                  <div className="relative group inline-block">
                                    <button
                                      disabled
                                      className="text-green-600 opacity-50 cursor-not-allowed"
                                      aria-label="Session completed"
                                    >
                                      <svg width="18" height="18" viewBox="0 0 25 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M2.5 10.7297L8.68692 17L22.5 3" stroke="#027A48" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </button>

                                    {/* Tooltip */}
                                    <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                      <div className="bg-white text-center text-black text-sm px-3 py-2 rounded-[8px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
                                        {item.statusChangedAt
                                          // ? `Completed on ${item.statusChangedAt}`
                                          ? <>Completed on <br /> {item.statusChangedAt}</>
                                          : 'Completed'}
                                      </div>
                                      <div className="w-2 h-2 bg-white rotate-45 absolute left-1/2 -translate-x-1/2 top-full -mt-1"></div>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {/* <div
                                      data-tooltip-id="session-tooltip"
                                      data-tooltip-content={!isCompanyActive ? "Company is inactive" : "Complete session"}
                                      className="inline-block"
                                    >
                                      <button
                                        onClick={() => {
                                          if (isCompanyActive) {
                                            openConfirmationModal(
                                              'Complete Session?',
                                              'Are you sure you want to mark this session as completed?',
                                              () => handleAssignStatus(item.id, true)
                                            );
                                          }
                                        }}
                                        disabled={!isCompanyActive}
                                        className={`text-green-600 ${!isCompanyActive ? 'opacity-50 cursor-not-allowed' : 'hover:text-green-700'}`}
                                        aria-label="Complete session"
                                      >
                                        <svg width="18" height="18" viewBox="0 0 25 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M2.5 10.7297L8.68692 17L22.5 3" stroke="#027A48" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      </button>
                                    </div>

                                    <div
                                      data-tooltip-id="session-tooltip"
                                      data-tooltip-content={!isCompanyActive ? "Company is inactive" : "Cancel session"}
                                      className="inline-block"
                                    >
                                      <button
                                        onClick={() => {
                                          if (isCompanyActive) {
                                            openConfirmationModal(
                                              'Cancel Session?',
                                              'Are you sure you want to cancel this session?',
                                              () => handleAssignStatus(item.id, false)
                                            );
                                          }
                                        }}
                                        disabled={!isCompanyActive}
                                        className={`text-red-600 ${!isCompanyActive ? 'opacity-50 cursor-not-allowed' : 'hover:text-red-700'}`}
                                        aria-label="Cancel session"
                                      >
                                        <svg width="16" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M13.6844 10L19.8296 3.88889C20.7235 3 20.7235 1.66667 19.8296 0.777778C19.4944 0.333333 18.9358 0 18.3771 0C17.8184 0 17.2598 0.222222 16.8129 0.666667L10.5559 6.88889L4.41061 0.777778C3.51676 -0.111111 2.06425 -0.111111 1.28212 0.777778C0.835195 1.11111 0.5 1.66667 0.5 2.33333C0.5 3 0.723464 3.44444 1.17039 3.88889L7.31564 10L1.17039 16.1111C0.835195 16.5556 0.5 17.1111 0.5 17.7778C0.5 18.3333 0.723464 18.8889 1.17039 19.3333C1.61732 19.7778 2.17598 20 2.73464 20C3.2933 20 3.85196 19.7778 4.29888 19.3333L10.4441 13.2222L16.5894 19.3333C17.4832 20.2222 18.9358 20.2222 19.7179 19.3333C20.6117 18.4444 20.6117 17 19.7179 16.2222L13.6844 10Z" fill="#B42318" />
                                        </svg>
                                      </button>
                                    </div> */}

                                    {/* Complete Session */}
                                    <div className="relative group inline-block">
                                      <button
                                        onClick={() => {
                                          if (isCompanyActive) {
                                            openConfirmationModal(
                                              'Complete Session?',
                                              'Are you sure you want to mark this session as completed?',
                                              () => handleAssignStatus(item.id, true)
                                            );
                                          }
                                        }}
                                        disabled={!isCompanyActive}
                                        className={`text-green-600 ${!isCompanyActive
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'hover:text-green-700'
                                          }`}
                                        aria-label="Complete session"
                                      >
                                        <svg width="18" height="18" viewBox="0 0 25 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M2.5 10.7297L8.68692 17L22.5 3" stroke="#027A48" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      </button>

                                      {/* Tooltip */}
                                      <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                        <div className="bg-white text-black text-sm px-3 py-2 rounded-[8px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
                                          {!isCompanyActive ? "Company is inactive" : "Complete session"}
                                        </div>
                                        <div className="w-2 h-2 bg-white rotate-45 absolute left-1/2 -translate-x-1/2 top-full -mt-1"></div>
                                      </div>
                                    </div>

                                    {/* Cancel Session */}
                                    <div className="relative group inline-block">
                                      <button
                                        onClick={() => {
                                          if (isCompanyActive) {
                                            openConfirmationModal(
                                              'Cancel Session?',
                                              'Are you sure you want to cancel this session?',
                                              () => handleAssignStatus(item.id, false)
                                            );
                                          }
                                        }}
                                        disabled={!isCompanyActive}
                                        className={`text-red-600 ${!isCompanyActive
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'hover:text-red-700'
                                          }`}
                                        aria-label="Cancel session"
                                      >
                                        <svg width="16" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M13.6844 10L19.8296 3.88889C20.7235 3 20.7235 1.66667 19.8296 0.777778C19.4944 0.333333 18.9358 0 18.3771 0C17.8184 0 17.2598 0.222222 16.8129 0.666667L10.5559 6.88889L4.41061 0.777778C3.51676 -0.111111 2.06425 -0.111111 1.28212 0.777778C0.835195 1.11111 0.5 1.66667 0.5 2.33333C0.5 3 0.723464 3.44444 1.17039 3.88889L7.31564 10L1.17039 16.1111C0.835195 16.5556 0.5 17.1111 0.5 17.7778C0.5 18.3333 0.723464 18.8889 1.17039 19.3333C1.61732 19.7778 2.17598 20 2.73464 20C3.2933 20 3.85196 19.7778 4.29888 19.3333L10.4441 13.2222L16.5894 19.3333C17.4832 20.2222 18.9358 20.2222 19.7179 19.3333C20.6117 18.4444 20.6117 17 19.7179 16.2222L13.6844 10Z" fill="#B42318" />
                                        </svg>
                                      </button>

                                      {/* Tooltip */}
                                      <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                        <div className="bg-white text-black text-sm px-3 py-2 rounded-[8px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
                                          {!isCompanyActive ? "Company is inactive" : "Cancel session"}
                                        </div>
                                        <div className="w-2 h-2 bg-white rotate-45 absolute left-1/2 -translate-x-1/2 top-full -mt-1"></div>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </Td>
                        <Td className="whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <CommonIconButton
                              type="view"
                              onClick={() => {
                                const name = item.sessionName || '';
                                if (name) dispatch(setsessionName(name));
                                navigate(`/companies/companyview?companyId=${companyId}&tab=sessions&SessionId=${item.id}&sessionTab=view`);
                              }} />

                            {item.sessionUrl && (
                              <p
                                className="w-10 h-10 flex items-center justify-center cursor-pointer"
                                onClick={() => {
                                  setSelectedSessionLink({ generateUrl: item.sessionUrl });
                                  setOpenCopyLinkModal(true);
                                }}
                              >
                                <CommonIconButton type="link" />
                              </p>
                            )}
                          </div>
                        </Td>
                      </Tr>
                    )
                  })
                ) : (
                  <Tr>
                    <Td colSpan={12} className="text-center font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                      No matching records found
                    </Td>
                  </Tr>
                )}
              </TBody>
            </Table>
          </div>
        </div>

        <div className="flex justify-between items-center mt-3">
          <Pagination
            totalCount={countData}
            pageNumber={getFilterData.page}
            displayPage={allSessionsData?.length}
            UpdatePageLimit={(limit) => SetFilterData((prev) => ({ ...prev, limit }))}
            UpdatePage={(page) => SetFilterData((prev) => ({ ...prev, page }))}
          />

          {countData > 0 && totalPages > 1 && (
            <ModernPagination
              totalPages={totalPages}
              currentPage={getFilterData.page}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </ToolManagmentCard>

      <Suspense fallback={null}>
        <AddEditSession dialogOpen={dialogOpen} onDialogClose={() => setDialogOpen(false)} companyId={companyId} refreshSessionsData={refreshSessionsData} />
        <AddEditSessionView
          dialogOpen={dialogOpenView}
          onDialogClose={() => setDialogOpenView(false)}
          isViewMode={isViewMode}
        />
      </Suspense>

      <CopyLinkURLModal
        dialogOpen={openCopyLinkModal}
        onDialogClose={() => setOpenCopyLinkModal(false)}
        onSubmit={() => setOpenCopyLinkModal(false)}
        companyData={selectedSessionLink}
      />

      <ConfirmationModal
        dialogOpen={confirmationModal.isOpen}
        onDialogClose={handleConfirmationNo}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={handleConfirmationYes}
        showYesNo={true}
      />
    </React.Fragment>
  );
};

export default BatchesSessions;